/**
 * Invalidates all four Manpower screen query keys (task 14.4;
 * `complan-manpower/design.md` section 8, R7.1, R7.2, R7.3, R7.4).
 *
 * A mutation on Capacity Inputs or the Effort Matrix invalidates both its own
 * query key and the Capacity & Utilisation / Demand Explorer keys, since an
 * assumption change can move numbers on all four screens at once - the same
 * rule Foundation's `useInvalidateCalendar()` follows for Initiative-timing
 * mutations.
 *
 * This hook mirrors Foundation's `useInvalidateCalendar()` pattern exactly:
 * returns a memoized callback that invalidates the entire `manpower` query key
 * namespace, so any Effort_Assumption, Capacity_Period or Initiative
 * demand-input mutation refreshes all four screens (Capacity & Utilisation,
 * Demand Explorer, Capacity Inputs, Effort Matrix) in one invalidation call.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { manpowerQueryKeys } from './manpower-query-keys.js';

/**
 * Returns a callback that invalidates all Manpower screen queries (R7.1, R7.2,
 * R7.3, R7.4).
 *
 * Call this after any Effort_Assumption, Capacity_Period or Initiative
 * demand-input mutation to refresh every screen that depends on those values.
 * The invalidation is scoped to the `manpower` namespace, so it touches only
 * the four Manpower screens and leaves the calendar and Initiative detail
 * queries unchanged.
 *
 * Wired into:
 * - `useCreateEffortAssumption` and `useDeactivateEffortAssumption` (effort-matrix-data.ts)
 * - `useCreateCapacityPeriod` and `useDeactivateCapacityPeriod` (capacity-inputs-data.ts)
 * - Initiative field force scope mutations (when demand-input fields are edited)
 */
export function useInvalidateManpowerViews(): () => Promise<void> {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: manpowerQueryKeys.all });
  }, [queryClient]);
}
